import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Center,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Spinner,
  Text,
  VStack,
  useToast,
  HStack,
  PinInput,
  PinInputField,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomInput from "../../components/UI/CustomInput";
import CustomButton from "../../components/UI/CustomButton";
import AuthLogo from "../../components/UI/AuthLogo";
import { useFormTheme } from "../../hooks/useFormTheme";
import { useAuth } from "../../context/AuthContext";
import FormErrorBox from "../../components/UI/FormErrorBox";
import { useTranslation } from "react-i18next";
import { getLocalizedAuthErrorMessage } from "../../helpers/authErrors";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const authCardTransition = {
  layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
};

interface ResetPasswordLocationState {
  email?: string;
}

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const {
    bg,
    errorColor,
    linkColor,
    pageBg,
    textColor,
    lightGradient,
    darkGradient,
  } = useFormTheme();
  const { confirmResetPasswordHandler, sendResetCodeHandler, isLoading } =
    useAuth();

  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const state = (location.state as ResetPasswordLocationState | null) ?? null;
  const targetEmail = state?.email ?? "";

  useEffect(() => {
    if (!targetEmail) {
      navigate("/forgot-password", { replace: true });
    }
  }, [targetEmail, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const fieldErrors = useMemo(() => Object.values(errors), [errors]);

  const validate = (
    field?: "code" | "password" | "confirmPassword",
    value?: string,
  ) => {
    const nextErrors: Record<string, string> =
      field && hasSubmitted ? { ...errors } : {};
    const nextCode = field === "code" ? (value ?? code) : code;
    const nextPassword = field === "password" ? (value ?? password) : password;
    const nextConfirmPassword =
      field === "confirmPassword"
        ? (value ?? confirmPassword)
        : confirmPassword;

    if (!nextCode || nextCode.length !== 6) {
      nextErrors.code = String(t("auth.verify.invalidCode"));
    } else {
      delete nextErrors.code;
    }

    if (!nextPassword || nextPassword.length < 6) {
      nextErrors.password = String(t("auth.common.passwordTooShort"));
    } else {
      delete nextErrors.password;
    }

    if (nextConfirmPassword !== nextPassword) {
      nextErrors.confirmPassword = String(t("auth.common.passwordsMustMatch"));
    } else {
      delete nextErrors.confirmPassword;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setHasSubmitted(true);

    if (!targetEmail) {
      navigate("/forgot-password", { replace: true });
      return;
    }

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      await confirmResetPasswordHandler(targetEmail, code, password);

      toast({
        title: String(t("auth.verify.resetSuccessTitle")),
        description: String(t("auth.verify.resetSuccessDescription")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });

      navigate("/login", { replace: true });
    } catch (error) {
      toast({
        title: String(t("auth.verify.errorTitle")),
        description: getLocalizedAuthErrorMessage(error, t),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || !targetEmail) return;

    setLoading(true);
    try {
      const lang = i18n.language?.toLowerCase().split("-")[0] || "fr";
      await sendResetCodeHandler(targetEmail, lang);
      setCode("");
      setResendCooldown(60);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.code;
        return next;
      });

      toast({
        title: String(t("auth.verify.resendCodeSuccess")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: String(t("auth.verify.resendCodeError")),
        description: getLocalizedAuthErrorMessage(error, t),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center position="relative" minH="100vh" overflow="hidden">
      <Box
        position="fixed"
        top={0}
        left={0}
        zIndex={-1}
        h="100vh"
        w="100vw"
        bgGradient={lightGradient}
        _dark={{ bgGradient: darkGradient }}
        bg={pageBg}
      />
      <MotionBox
        maxW={{ base: "90%", md: "400px", "2xl": "450px" }}
        px={{ base: "24px", md: "40px" }}
        w="full"
        boxShadow="md"
        borderRadius="md"
        py="44px"
        bg={bg}
        layout
        transition={authCardTransition}
      >
        <AuthLogo />

        <Center mb={6}>
          <Text fontSize="16px" fontWeight="medium" color={textColor}>
            {t("auth.resetPassword.title")}
          </Text>
        </Center>

        <form onSubmit={handleSubmit} autoComplete="off">
          <VStack spacing={{ base: "24px", md: "32px" }} align="stretch">
            <FormControl isInvalid={!!errors.code}>
              <FormLabel>{t("auth.verify.codeLabel")}</FormLabel>
              <HStack>
                <PinInput
                  otp
                  size={{ base: "md", md: "lg" }}
                  focusBorderColor={errors.code ? "red.500" : "primary.500"}
                  errorBorderColor="red.500"
                  onChange={(value) => {
                    setCode(value);
                    if (hasSubmitted) {
                      validate("code", value);
                    }
                  }}
                  value={code}
                >
                  <PinInputField
                    borderColor={errors.code ? "red.500" : undefined}
                  />
                  <PinInputField
                    borderColor={errors.code ? "red.500" : undefined}
                  />
                  <PinInputField
                    borderColor={errors.code ? "red.500" : undefined}
                  />
                  <PinInputField
                    borderColor={errors.code ? "red.500" : undefined}
                  />
                  <PinInputField
                    borderColor={errors.code ? "red.500" : undefined}
                  />
                  <PinInputField
                    borderColor={errors.code ? "red.500" : undefined}
                  />
                </PinInput>
              </HStack>
              <FormErrorMessage color={errorColor}>
                {errors.code}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.password}>
              <FormLabel>{t("auth.register.password")}</FormLabel>
              <CustomInput
                type="password"
                error={!!errors.password}
                placeholder={String(t("auth.register.passwordPlaceholder"))}
                value={password}
                onChange={(e) => {
                  const value = e.target.value;
                  setPassword(value);
                  if (hasSubmitted) {
                    validate("password", value);
                  }
                }}
              />
              <FormErrorMessage color={errorColor}>
                {errors.password}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.confirmPassword}>
              <FormLabel>{t("auth.register.confirmPassword")}</FormLabel>
              <CustomInput
                type="password"
                error={!!errors.confirmPassword}
                placeholder={String(
                  t("auth.register.confirmPasswordPlaceholder"),
                )}
                value={confirmPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  setConfirmPassword(value);
                  if (hasSubmitted) {
                    validate("confirmPassword", value);
                  }
                }}
              />
              <FormErrorMessage color={errorColor}>
                {errors.confirmPassword}
              </FormErrorMessage>
            </FormControl>

            <FormErrorBox errors={fieldErrors} />

            <CustomButton type="submit" isDisabled={loading || isLoading}>
              {loading || isLoading ? (
                <Spinner size="sm" />
              ) : (
                t("auth.resetPassword.submit")
              )}
            </CustomButton>

            <Center>
              <Button
                variant="ghost"
                size="sm"
                fontSize="14px"
                color={resendCooldown > 0 ? "gray.400" : linkColor}
                isDisabled={resendCooldown > 0 || loading || isLoading}
                onClick={handleResendCode}
                _hover={{ textDecor: "underline", bg: "transparent" }}
              >
                {resendCooldown > 0
                  ? `${String(t("auth.verify.resendCode"))} (${resendCooldown}s)`
                  : String(t("auth.verify.resendCode"))}
              </Button>
            </Center>

            <Center>
              <Text
                fontSize="14px"
                color={linkColor}
                textDecor="underline"
                cursor="pointer"
                onClick={() => navigate("/login")}
              >
                {t("auth.verify.backToLogin")}
              </Text>
            </Center>
          </VStack>
        </form>
      </MotionBox>
    </Center>
  );
};

export default ResetPasswordPage;
